import React, { ChangeEvent } from 'react';
import { Form, Button, Alert } from 'react-bootstrap';
import { AIServiceConfig, AIServiceConfigValidation, QNA_MAX_CONTEXT_MESSAGES } from './services';

interface QnAConfigAdvancedTabProps {
  config: AIServiceConfig;
  validation: AIServiceConfigValidation;
  importFile: File | null;
  showResetConfirm: boolean;
  onFieldChange: (field: keyof AIServiceConfig, value: unknown) => void;
  onFileChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onExport: () => void;
  onImport: () => void;
  onResetClick: () => void;
  onResetConfirm: () => void;
  onResetCancel: () => void;
  renderValidationFeedback: (field: keyof AIServiceConfigValidation) => React.ReactNode;
}

export function QnAConfigAdvancedTab({
  config,
  validation,
  importFile,
  showResetConfirm,
  onFieldChange,
  onFileChange,
  onExport,
  onImport,
  onResetClick,
  onResetConfirm,
  onResetCancel,
  renderValidationFeedback,
}: QnAConfigAdvancedTabProps) {
  return (
    <div className="mt-3">
      <Form.Group className="mb-3">
        <Form.Label>Max Context Messages</Form.Label>
        <Form.Control
          type="number"
          min={1}
          max={QNA_MAX_CONTEXT_MESSAGES}
          value={config.maxContextMessages}
          onChange={(e) => onFieldChange('maxContextMessages', parseInt(e.target.value, 10))}
          isInvalid={!validation.maxContextMessages.isValid}
        />
        {renderValidationFeedback('maxContextMessages')}
        <Form.Text className="text-muted">
          <strong>Frontend only:</strong>
          {' '}
          Limits UI message history to prevent memory leak. Maximum number of recent messages to include in AI context (1-
          {QNA_MAX_CONTEXT_MESSAGES}
          ).
        </Form.Text>
      </Form.Group>

      <hr />

      <h6>Import/Export Configuration</h6>

      <div className="d-flex gap-2 mb-3">
        <Button variant="outline-secondary" onClick={onExport}>
          Export
        </Button>

        <div className="d-flex align-items-center">
          <Form.Control
            type="file"
            accept=".json"
            onChange={onFileChange}
            className="me-2"
            style={{ width: 'auto' }}
          />
          <Button
            variant="outline-primary"
            onClick={onImport}
            disabled={!importFile}
          >
            Import
          </Button>
        </div>
      </div>

      <Button
        variant="outline-danger"
        onClick={onResetClick}
      >
        Reset to Factory Defaults
      </Button>

      {showResetConfirm && (
        <Alert variant="warning" className="mt-3">
          <Alert.Heading>Reset Confirmation</Alert.Heading>
          <p>This action will reset all settings to default values. Continue?</p>
          <div className="d-flex gap-2">
            <Button variant="danger" size="sm" onClick={onResetConfirm}>
              Yes, Reset
            </Button>
            <Button variant="secondary" size="sm" onClick={onResetCancel}>
              Cancel
            </Button>
          </div>
        </Alert>
      )}
    </div>
  );
}
